import { useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';

import { COEFFICIENT_DIGITAL_HEIGHT } from 'components/widgets/Rail/constants';
import {
  calculateItemHeight,
  calculateTotalWidth,
} from 'components/widgets/Rail/utils/calculateItemDimensions/calculateItemDimensions';
import { WIDGET_TYPE } from 'consts';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { getImageSizeParams } from 'utils/getImageSizeParams';

import { useApplyIncreaseHeightHook } from '../useApplyIncreaseHeightHook/useApplyIncreaseHeightHook';
import { useCheckResizeHook } from '../useCheckResizeHook/useCheckResizeHook';
import { useOnCardClickHook } from '../useOnCardClickHook/useOnCardClickHook';
import { useOnHoverHook } from '../useOnHoverHook/useOnHoverHook';
import { useRailLastXPosition } from '../useRailLastXPosition/useRailLastXPosition';

function useRailProviderCombiner<T extends { id: number }>(pageReferenceId?: string, referenceId?: string, hasAssetsMarkedByNumber?: boolean) {
  const { data: railData, settings: railSettings, type: railType } = useWidgetContext<T>();
  const savedOffset = useRailLastXPosition({ railId: railSettings.id!, railType, items: railData.items });
  const offset = useRef(savedOffset);
  const isLogoRail = railType === WIDGET_TYPE.LOGO;
  const headerRef = useRef<HTMLElement | null>(null);
  const railCardsListRef = useRef<List | null>(null);
  const refElementInner = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const setRailCardsListRefScrollTo = useCallback((value: number) => {
    if (railCardsListRef.current) {
      railCardsListRef.current.scrollTo(value);
      offset.current = value;
    }
  }, []);
  const { imageHeight, imageWidth } = getImageSizeParams(railSettings); // should be removed after fallback params becomes required
  const componentHeight = calculateItemHeight(railSettings, isLogoRail);
  const totalWidth = calculateTotalWidth(railData.items.length, hasAssetsMarkedByNumber ? (imageWidth + (imageHeight / COEFFICIENT_DIGITAL_HEIGHT)) : imageWidth);
  const checkResize = useCheckResizeHook({
    setRailCardsListRefScrollTo,
    refElementInner,
    offset,
    totalWidth,
  });
  const onHover = useOnHoverHook({ headerRef, refElementInner, componentHeight });
  const onCardClick = useOnCardClickHook({ id: railSettings.id!, pageReferenceId, referenceId, offset });
  const nextButtons = useRef({
    isLeftNextButton: offset.current > 0,
    isRightNextButton:
      // @ts-ignore
      offset.current + (refElementInner?.current?.clientWidth || document.body.clientWidth) < totalWidth,
  });
  const setNextButtons = useCallback((value: any) => {
    nextButtons.current = value;
  }, []);

  useApplyIncreaseHeightHook({ refElementInner, componentHeight });

  return {
    checkResize,
    onHover,
    onCardClick,
    wrapperRef,
    totalWidth,
    componentHeight,
    headerRef,
    refElementInner,
    railCardsListRef,
    offset,
    setNextButtons,
    setRailCardsListRefScrollTo,
    nextButtons,
    isLogoRail,
    savedOffset,
    imageHeight,
    imageWidth,
  };
}

export { useRailProviderCombiner };
